import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import {
  RoomEventResponse,
  OpeningResponse,
  NotificationResponse,
  ModelUpdatingResponse,
  DataRequestResponse,
  SocketCloseEventResponse
} from "./types"
import { ConsoleLogger } from "../util/logger"
import { chatIdsSelector } from "../recoil/atoms/chats"
import {
  userIdsSelector,
  localUserStaticPropsAtom
} from "../recoil/atoms/users"
import { badgeIdsSelector } from "../recoil/atoms/badges"
import { bannerIdsSelector } from "../recoil/atoms/banners"
import { streamsAtom, middlewareStream } from "../recoil/atoms/streams"
import {
  watchPartyHistoryLinksSelector,
  middlewareWatchPartyHistoryLinks
} from "../recoil/atoms/watchPartyHistoryLinks"
import { useEventsCB } from "./useEventsCB"
import {
  watchPartyScreenLinkSelector,
  watchPartyScreenSelector,
  watchPartyHostIdSelector,
  watchPartyCallbackSelector
} from "../recoil/atoms/watchPartyScreen"
import { usePictureInPicture } from "../hooks/usePictureInPicture"
import { playerPropsSelector } from "../recoil/atoms/player"
import {
  addCurrentQueryParams,
  checkIsModNotPresentId,
  hasPermission
} from "../util"
import layoutStates from "../recoil/atoms/layout"
import { stopAudienceDisconnectTimer } from "../util/disconnectAudienceUser"
import { badgeHisrotyIdsSelector } from "../recoil/atoms/badgeHistory"
import { overlaySelector } from "../recoil/atoms/overlay"
import { auctionParamsSelector } from "../recoil/atoms/auction"
import { viewSelector } from "../recoil/atoms/view"
import { HIGH_VIDEO_STREAM_TYPE, Routes, Views } from "../constants"
import { AppStatus, ConnectionStatus } from "../constants/enums"
import useCustomResetRecoilState from "../hooks/useCustomResetRecoilState"
import { handleWebsocketDisconnectSelector } from "../recoil/atoms/wsStatus"
import { MainVideoStreamTypeAtom } from "../recoil/atoms/remoteVideoStreamType"
import { roomLinksSelector } from "../recoil/atoms/roomLinks"
import { roomSelector } from "../recoil/atoms/room"
import { shoppableMNPVideoAtom } from "../recoil/atoms/shoppableVideos"
import useHighQualityStreamSettings from "../hooks/useHighQualityStreamSettings"
import { useCallback, useContext, useRef } from "react"
import { eventTrackerSourceAtom } from "../recoil/atoms/eventTracker"
import { taggedBadgesAtom } from "../recoil/atoms/taggedBadges"
import { connectionStatusAtom } from "../recoil/atoms/connectionStatus"
import { sessionEndPageAtom } from "src/recoil/atoms/sessionModalData"
import useConfig, { getConfigs } from "src/hooks/useConfig"
import { t } from "i18next"
import { navigatedFromAvatarsListAtom } from "src/recoil/atoms/avatars"
import { SDKContext } from "./SDKContext"
import MOLocation from "src/util/MOLocation"
import { useHistory } from "react-router-dom"

interface WsEventsCBProps {
  onMogConnected: (response: OpeningResponse) => Promise<void>
  onMessage: (response: any) => void
  onError: (response: RoomEventResponse) => void
  onClose: (response: SocketCloseEventResponse) => void
  onEventSourceChanged: (response: any) => void
}

const logger = new ConsoleLogger()
const MeetOnline = window.MeetOnline
const MessageType = MeetOnline?.MessageType
const UserPermission = window?.MeetOnline?.UserPermission

export const useWsEventsCB = (): WsEventsCBProps => {
  const setChats = useSetRecoilState(chatIdsSelector)
  const setLocalUserStaticProps = useSetRecoilState(localUserStaticPropsAtom)
  const setUsers = useSetRecoilState(userIdsSelector)
  const setBanners = useSetRecoilState(bannerIdsSelector)
  const setBadges = useSetRecoilState(badgeIdsSelector)
  const setOverlay = useSetRecoilState(overlaySelector)
  const setBadgeHistory = useSetRecoilState(badgeHisrotyIdsSelector)
  const setStreamsList = useSetRecoilState(streamsAtom)
  const setRoom = useSetRecoilState(roomSelector)
  const setAuctionParams = useSetRecoilState(auctionParamsSelector)
  const setMainVideoStreamType = useSetRecoilState(MainVideoStreamTypeAtom)
  const setRoomLinks = useSetRecoilState(roomLinksSelector)
  const setShoppableMNPVideo = useSetRecoilState(shoppableMNPVideoAtom)
  const setTaggedBadges = useSetRecoilState(taggedBadgesAtom)
  const { setAudienceBuffering, setStreamingResolution, setRtmpStreamEnable } =
    useHighQualityStreamSettings()
  const [sessionEndPage, setSessionEndPage] = useRecoilState(sessionEndPageAtom)
  const [_, setNavigatedFromAvatarsList] = useRecoilState(
    navigatedFromAvatarsListAtom
  )

  const setWatchPartyHistoryLinks = useSetRecoilState(
    watchPartyHistoryLinksSelector
  )
  const setWatchPartyScreen = useSetRecoilState(watchPartyScreenSelector)
  const setWatchPartyScreenLink = useSetRecoilState(
    watchPartyScreenLinkSelector
  )
  const setWatchPartyHostId = useSetRecoilState(watchPartyHostIdSelector)
  const { exitPictureInPicture } = usePictureInPicture()
  const setPlayerProps = useSetRecoilState(playerPropsSelector)
  const setTargetTabIndex = useSetRecoilState<number>(
    layoutStates.historyTabEnabled
  )
  const setView = useSetRecoilState(viewSelector)
  const handleWebsocketDisconnect = useSetRecoilState(
    handleWebsocketDisconnectSelector
  )
  const resetRecoilState = useCustomResetRecoilState()
  const setEventTrackerSource = useSetRecoilState(eventTrackerSourceAtom)
  const setConnectionStatus = useSetRecoilState(connectionStatusAtom)
  const watchPartyCallback = useRecoilValue(watchPartyCallbackSelector)
  const meetingClient = useContext(SDKContext)

  const config = useConfig()
  const configRef = useRef(config)
  configRef.current = config
  const history = useHistory()

  const {
    handleDataRequest,
    handleMediaConnect,
    handleModelUpdate,
    handleNotification,
    handleRecordingRequest,
    handleStreamingRequest,
    handleHeartbeatUpdate,
    handlePrivateRoomRequest
  } = useEventsCB()

  // Handle Watch Party Progress
  const handleWatchPartyProgress = async (watchPartyInProgress: boolean) => {
    const wphostId = await watchPartyCallback.getWatchPartyHostId()
    // if watchPartyInProgress === false then stop current watch party video
    if (
      !watchPartyInProgress &&
      wphostId &&
      !checkIsModNotPresentId(wphostId)
    ) {
      setWatchPartyHistoryLinks(
        middlewareWatchPartyHistoryLinks({ type: "RESET", payload: "" })
      )
      stopServerWatchParty()
      setTaggedBadges([])
    }
  }

  const handleWebsocketOnMogConnected = async (response: OpeningResponse) => {
    const client = window.MeetOnline.client()
    const { permissions, id } = response.data.user

    // Set connection status to CONNECTED
    setConnectionStatus(ConnectionStatus.CONNECTED)

    handleWatchPartyProgress(response.data?.watchPartyInProgress)

    // Set showInteractivityRequiredVisual property
    if (response.data?.showInteractivityRequiredVisual !== undefined) {
      setRoom({
        name: "UPDATE",
        attributes: {
          showInteractivityRequiredVisual:
            response.data.showInteractivityRequiredVisual
        }
      })
    }

    // High quality streaming setting
    setRtmpStreamEnable(response.data?.rtmpStreamEnable)
    setAudienceBuffering(response.data?.audienceBuffering)
    setStreamingResolution(response.data?.streamingResolution)

    // disable the mic and cam depending on permission
    if (
      !hasPermission(permissions, UserPermission.canUseMic) &&
      client?._joinPageMicEnabled
    ) {
      client.setJoinPageMicEnabled(false)
      client.toggleMic(true, id)
    }

    if (
      !hasPermission(permissions, UserPermission.canUseCam) &&
      client?._joinPageCamEnabled
    ) {
      client.setJoinPageCamEnabled(false)
      client.toggleCam(true, id)
    }

    setUsers({ name: "ADD", users: [response.data.user] })
    setLocalUserStaticProps({
      id: response.data.user.id,
      name: response.data.user.name
    })
    setRoomLinks({
      audienceHash: response.data.audienceHash,
      broadcasterHash: response.data.broadcasterHash,
      moderatorHash: ""
    })

    // Inform the native app that user has join the meeting
    client?.nativeBridge.eventUserJoinedMeeting()

    const conf = await getConfigs()
    if (
      conf.postLoginRedirectUrl !== "" &&
      conf.postLoginRedirectUrl !== "loading"
    ) {
      MOLocation.init()
      MOLocation.hideURL(addCurrentQueryParams(conf.postLoginRedirectUrl))
    }
  }

  const handleWebsocketOnMessage = (response: any) => {
    let { messageType } = response

    switch (messageType) {
      case MessageType.connect:
        handleMediaConnect()
        break
      case MessageType.heartbeat:
        handleHeartbeatUpdate(response)
        break
      case MessageType.notification:
        handleNotification(response as NotificationResponse)
        break
      case MessageType.modelUpdate:
        handleModelUpdate(response as ModelUpdatingResponse)
        break
      case MessageType.dataRequest:
        handleDataRequest(response as DataRequestResponse)
        break
      case MessageType.recording:
        handleRecordingRequest(response as DataRequestResponse)
        break
      case MessageType.streaming:
        handleStreamingRequest(response as DataRequestResponse)
        break
      case MessageType.privateRoom:
        handlePrivateRoomRequest(response as DataRequestResponse)
        break
      default:
        handleModelUpdate(response as ModelUpdatingResponse)
    }
  }

  const handleWebsocketOnError = (response: RoomEventResponse) => {
    logger.log("CB----handleWebsocketOnError----", response)
  }

  const stopServerWatchParty = async () => {
    setWatchPartyScreen(false)
    setWatchPartyScreenLink("")
    setWatchPartyHostId("")
    exitPictureInPicture()
    setPlayerProps({ isControlVolume: false })
    setShoppableMNPVideo({
      idShoppableMNP: "",
      enabledShoppableMNP: false,
      srcShoppableMNP: "",
      typeShoppableMNP: ""
    })
    await MeetOnline?.client().rtc?.stopWatchParty()
  }

  const handleWebsocketOnClose = async (response: SocketCloseEventResponse) => {
    logger.log("Client CB: Websocket onClose", response)
    console.log("Client CB: Websocket onClose", response)
    console.log("code", +response?.code)
    console.log("window.MeetOnline.AppStatus", window.MeetOnline.AppStatus)
    const { postLoginRedirectUrl } = await getConfigs()
    switch (+response?.code) {
      case AppStatus.WEBSOCKET_PING_TIMEOUT:
        handleWebsocketDisconnect({ reason: +response?.code })
        setView(Views.HOME)
        break

      // Reconnecting
      case AppStatus.WEBSOCKET_AUTH_RECONNECTION:
      case AppStatus.NETWORK_ERROR:
        logger.log("Reconnecting...")
        // Set connection status to RECONNECTING
        setConnectionStatus(ConnectionStatus.RECONNECTING)
        resetRecoilState({ reconnecting: true })
        return
      case AppStatus.MISSING_ENTITLEMENT:
        // setView(Views.PLANS_VIEW)
        history.replace(Routes.PLANS)
        break
      case AppStatus.RESTRICTED_PERSONAL_ROOM:
        setNavigatedFromAvatarsList(false)
        setSessionEndPage({
          sessionEndPageCloseButtonLink: postLoginRedirectUrl,
          sessionEndPageCloseButtonText: t("Go Back"),
          sessionEndPageTitle: t("Failed to join personal room"),
          sessionEndPageMessage: t(
            "Ensure that you are signed in with the correct account to access this room."
          )
        })
        setView(Views.BYE)
        break
      case AppStatus.ONE_USER_LIMIT_PERSONAL_ROOM:
        setNavigatedFromAvatarsList(false)
        setSessionEndPage({
          sessionEndPageCloseButtonLink: postLoginRedirectUrl,
          sessionEndPageCloseButtonText: t("Go Back"),
          sessionEndPageTitle: t(
            "Your account joined your room from another device"
          ),
          sessionEndPageMessage: t(
            "Check whether you have any other devices/tabs trying to join the room. If this action was not performed by you, consider changing your password."
          )
        })
        setView(Views.BYE)
        break
      default:
        // navigate user to bye page
        if (config.postLoginRedirectUrl !== "") {
          // history.replace(config.postLoginRedirectUrl)
        } else {
          setView(Views.BYE)
        }
        break
    }

    if (response?.code !== undefined && +response?.code !== 0) {
      meetingClient?.disconnectGateway(0)
    }
    // Set connection status to DISCONNECTED
    setConnectionStatus(ConnectionStatus.DISCONNECTED)

    // Reset all recoil state to default value
    resetRecoilState()

    // TODO: Also move all the below state in above function

    setChats({ type: "RESET" })

    // Reset the data
    setUsers({ name: "RESET" })
    setAuctionParams({ action: "reset" })
    setStreamsList(middlewareStream({ type: "RESET", payload: "" }))

    // Reset overlay
    setOverlay(null)
    setBadges({ name: "RESET" })
    setBanners({ name: "RESET" })
    setBadgeHistory({ name: "RESET" })

    // Reset app setting
    setTargetTabIndex(0)
    setMainVideoStreamType(HIGH_VIDEO_STREAM_TYPE)
    stopAudienceDisconnectTimer(true)
    setRoom({
      name: "UPDATE",
      attributes: {
        showInteractivityRequiredVisual: false
      }
    })

    // Stop watch party
    setWatchPartyHistoryLinks(
      middlewareWatchPartyHistoryLinks({ type: "RESET", payload: "" })
    )
    stopServerWatchParty()
    setTaggedBadges([])
  }

  const onEventSourceChanged = useCallback(
    response => {
      setEventTrackerSource(response)
    },
    [setEventTrackerSource]
  )

  return {
    onMogConnected: handleWebsocketOnMogConnected,
    onMessage: handleWebsocketOnMessage,
    onError: handleWebsocketOnError,
    onClose: handleWebsocketOnClose,
    onEventSourceChanged
  }
}
